/**
 * Edupra brand assets — yantra-grid mark family.
 *
 * Logomark concept: a 4×4 lattice of small squares with a single emerald
 * cell at row 2 col 1 (off-centre). Reads as: a school register / ledger
 * page (rows × columns of records), a database table (one cell highlighted
 * = the active entry), a calendar grid.
 *
 * Sizes:
 *   - 64 px (hero) — 4×4 grid, generous gutters
 *   - 32 px (chrome) — 4×4 grid, tight gutters
 *   - 16 px (favicon) — collapses to 3×3 grid
 *
 * Wordmark: lowercase "edupra" in Geist Medium with -0.035em tracking.
 */
import { Link } from 'react-router';
import { type CSSProperties } from 'react';

interface MarkProps {
  size?: number;
  className?: string;
  accent?: { row: number; col: number };
}

/** 4×4 yantra-grid — the canonical mark. Use ≥ 24 px. */
export function Mark4x4({ size = 32, className, accent = { row: 2, col: 1 } }: MarkProps) {
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) cells.push([r, c]);
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      role="img"
      aria-label="Edupra"
      className={className}
    >
      {cells.map(([r, c]) => {
        const x = 2 + c * 7;
        const y = 2 + r * 7;
        const isAccent = r === accent.row && c === accent.col;
        return (
          <rect
            key={`${r}-${c}`}
            x={x}
            y={y}
            width={5}
            height={5}
            rx={0.75}
            style={{ fill: isAccent ? 'var(--color-accent-500)' : 'currentColor' }}
          />
        );
      })}
    </svg>
  );
}

/** 3×3 reduction for the favicon — same family, fewer cells. */
export function Mark3x3({ size = 16, className, accent = { row: 1, col: 1 } }: MarkProps) {
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) cells.push([r, c]);
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      role="img"
      aria-label="Edupra"
      className={className}
    >
      {cells.map(([r, c]) => {
        const x = 1.25 + c * 4.75;
        const y = 1.25 + r * 4.75;
        const isAccent = r === accent.row && c === accent.col;
        return (
          <rect
            key={`${r}-${c}`}
            x={x}
            y={y}
            width={3.5}
            height={3.5}
            rx={0.5}
            style={{ fill: isAccent ? 'var(--color-accent-500)' : 'currentColor' }}
          />
        );
      })}
    </svg>
  );
}

/** Auto-size-based picker — uses 3×3 for ≤ 18 px, 4×4 above. */
export function Logomark({ size = 28, className, accent }: MarkProps) {
  if (size <= 18) return <Mark3x3 size={size} className={className} accent={accent} />;
  return <Mark4x4 size={size} className={className} accent={accent} />;
}

type WordmarkWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type WordmarkCase = 'lower' | 'sentence' | 'upper';

interface WordmarkProps {
  size?: number;
  weight?: WordmarkWeight;
  case?: WordmarkCase;
  tracking?: number;
  className?: string;
  color?: string;
}

const FONT_WEIGHTS: Record<WordmarkWeight, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export function Wordmark({
  size = 18,
  weight = 'medium',
  case: textCase = 'lower',
  tracking = -0.035,
  className,
  color,
}: WordmarkProps) {
  const text = textCase === 'upper' ? 'EDUPRA' : textCase === 'sentence' ? 'Edupra' : 'edupra';
  const style: CSSProperties = {
    fontFamily: 'Geist, system-ui, sans-serif',
    fontWeight: FONT_WEIGHTS[weight],
    letterSpacing: `${tracking}em`,
    fontSize: `${size}px`,
    color: color ?? 'var(--fg-high)',
    lineHeight: 1,
    fontFeatureSettings: '"ss01"',
    display: 'inline-block',
  };
  return <span className={className} style={style}>{text}</span>;
}

interface BrandProps {
  variant?: 'full' | 'mark';
  size?: number;
  href?: string | null;
  className?: string;
  weight?: WordmarkWeight;
  /** Override the wordmark colour (the mark uses currentColor). */
  color?: string;
  accent?: { row: number; col: number };
}

export function Brand({
  variant = 'full',
  size = 28,
  href = '/',
  className,
  weight = 'medium',
  color,
  accent,
}: BrandProps) {
  const inner = (
    <span className="inline-flex items-center gap-2 select-none" style={color ? { color } : undefined}>
      <Logomark size={size} accent={accent} />
      {variant === 'full' && <Wordmark size={Math.round(size * 0.7)} weight={weight} color={color} />}
    </span>
  );
  if (!href) return <span className={className}>{inner}</span>;
  return (
    <Link to={href} className={className} aria-label="Edupra home">
      {inner}
    </Link>
  );
}
